import { Helmet } from 'react-helmet-async'
import { useIntl } from 'react-intl'
import { Link, useSearchParams } from 'react-router-dom'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { Typography } from '@/app/components/Typography'
import { MydraDuoLogo } from '@/app/icons/MydraDuoLogo/MydraDuoLogo'
import { getRedirectBaseUrl, isValidRedirect } from '@/app/lib/auth'
import { ROUTES } from '@/app/lib/constants'
import { appendLocaleParam } from '@/app/lib/locale'
import { useAuth } from '@/app/providers/Auth/Auth'
import { useMemo } from 'react'
import * as CookieConsent from 'vanilla-cookieconsent'

export function MePage() {
  const intl = useIntl()

  const { firstName, lastName, email } = useAuth()
  const [searchParams] = useSearchParams()

  const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase()

  const product = searchParams.get('product') ?? ''
  const redirectTo = searchParams.get('redirectTo') ?? ''

  const baseUrl = getRedirectBaseUrl(redirectTo, product)

  const continueUrl = useMemo(() => {
    const url = new URL(redirectTo, baseUrl)

    if (!isValidRedirect(url.toString())) {
      return appendLocaleParam(baseUrl)
    }

    return appendLocaleParam(url.toString())
  }, [redirectTo, baseUrl])

  const logoutUrl = useMemo(() => {
    const params = new URLSearchParams()
    if (product) params.set('product', product)
    if (redirectTo) params.set('redirectTo', redirectTo)
    const qs = params.toString()
    return qs ? `${ROUTES.logout}?${qs}` : ROUTES.logout
  }, [product, redirectTo])

  return (
    <>
      <Helmet>
        <style>
          {` .mydra-base-layout { min-height: 0; height: 100vh; } `}
        </style>
      </Helmet>
      <div className="relative flex flex-col h-full md:flex-row">
        <div className="flex flex-col gap-8 justify-center items-center p-8 text-center md:w-1/2 md:flex">
          <MydraDuoLogo className="mx-auto mt-12 h-8 md:mt-7 md:ml-9" />
          <div className="flex flex-col w-full gap-4 bg-white md:my-auto md:w-96 md:mx-auto">
            <Typography variant="h2">
              {intl.formatMessage({
                id: 'Me.already-logged-in',
                defaultMessage: 'Already logged in',
              })}
            </Typography>

            <div className="flex items-center p-4 bg-gray-50 rounded-lg shadow-sm max-w-md">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-11 h-11 rounded-full bg-purple-500 flex items-center justify-center text-white text-base font-semibold">
                    {initials}
                  </div>
                </div>
                <div className="flex flex-col">
                  <h2 className="text-base font-semibold text-gray-800">
                    {`${firstName} ${lastName}`}
                  </h2>
                  <p className="text-sm text-gray-600">{email}</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 md:gap-4">
              <PrimaryButton className="w-full" href={continueUrl}>
                {intl.formatMessage({ id: 'Me.continue-as' }, { firstName })}
              </PrimaryButton>
              <p className="mt-1 text-xs md:text-sm text-center">
                <Link
                  to={logoutUrl}
                  className="font-semibold text-center text-mydra-black"
                >
                  {intl.formatMessage({
                    id: 'Me.logout',
                    defaultMessage: 'Logout',
                  })}
                </Link>
              </p>
              <button
                type="button"
                className="absolute bottom-0 right-0 p-4 text-sm "
                onClick={() => {
                  CookieConsent.showPreferences()
                }}
              >
                Cookie preferences
              </button>
            </div>
          </div>
        </div>
        <div className="relative flex-col items-center justify-center hidden gap-8 p-8 text-center md:w-1/2 md:flex">
          <div className="absolute w-full h-full p-2">
            <img
              className="object-cover w-full h-full rounded-xl"
              src="/images/static/img/hero_bg.jpg"
              alt="Hero background"
            />
          </div>
        </div>
      </div>
    </>
  )
}
